"use client"
import PostCardItem from "@/(admin)/modules/ui-builder/config/blocks/PostGrid/PostCardItem"
import { useUIBuilderLocaleStore } from "@/(admin)/modules/ui-builder/store/useUIBuilderLocaleStore"
import { fetchPostByCategories } from "@/services/postServices"
import { getSiteIdFromHost } from "@/sites/config"
import { useDomainStore } from "@/stores/use-domain-store"
import { getEnforcedDomainValue } from "@/utils/admin-domain-filter"
import { ComponentConfig } from "@measured/puck"
import { useParams } from "next/navigation"
import { useEffect, useState } from "react"

export type PostGridByCategoryProps = {
  idCategory: string[]
  /**
   * Domain lưu kèm block, tự động lấy theo site đang chọn ở admin lúc sửa.
   * Public đọc lại để filter post đúng site (host public gộp foods vào bucket
   * group nên không tự suy ra được).
   */
  websiteDomain?: string
}

export const RenderConfig: ComponentConfig<PostGridByCategoryProps> = {
  render: ({ idCategory, websiteDomain, puck }) => {
    const params = useParams()
    const storeLocale = useUIBuilderLocaleStore(state => state.locale)
    const { selectedDomain } = useDomainStore()

    // params.locale phải đứng trước: storeLocale mặc định "vi" (luôn truthy) nên
    // nếu để trước sẽ nuốt locale của URL và khoá block ở tiếng Việt trên public.
    const currentLocale = (params?.locale as string) || storeLocale || "vi"

    // `domain-storage` được persist vào COOKIE cùng origin, nên trang public cũng
    // đọc được site mà admin đang chọn -> nếu tin adminDomain vô điều kiện thì mở
    // admin chọn site khác sẽ khiến block public filter sai domain và rỗng data.
    // Vì vậy chỉ dùng adminDomain khi THỰC SỰ đang sửa trong editor (puck.isEditing);
    // trên public đọc websiteDomain đã lưu kèm block, content cũ chưa có thì fallback
    // siteId theo host (giữ hành vi cũ).
    const adminDomain = puck?.isEditing ? getEnforcedDomainValue(selectedDomain) : ""
    const effectiveDomain = adminDomain || websiteDomain || ""
    const host = typeof window !== "undefined" ? window.location.host : ""
    const siteId = getSiteIdFromHost(host)

    const [currentPagePosts, setCurrentPagePosts] = useState<any[]>([])
    const [totalPosts, setTotalPosts] = useState(0)
    const [currentPage, setCurrentPage] = useState(1)
    const [postsPerPage] = useState(20)
    const [loading, setLoading] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)

    const fetchPosts = async (categoryIds: string[], page: number = 1, append: boolean = false) => {
      if (categoryIds.length === 0) {
        setCurrentPagePosts([])
        setTotalPosts(0)
        return
      }

      if (append) {
        setLoadingMore(true)
      } else {
        setLoading(true)
      }

      try {
        const paginationParams = {
          page: page.toString(),
          pageSize: postsPerPage.toString(),
          locale: currentLocale,
          // domain (chính xác) thắng siteId (bucket) trong fetchPostByCategories.
          ...(effectiveDomain ? { domain: effectiveDomain } : { siteId }),
        }

        const response = await fetchPostByCategories(categoryIds, paginationParams)
        if (response) {
          if (append && page > 1) {
            setCurrentPagePosts(prev => [...prev, ...response.data])
          } else {
            setCurrentPagePosts(response.data)
          }
          setTotalPosts(response.meta?.pagination?.total || response.data.length)
        } else {
          if (!append) {
            setCurrentPagePosts([])
            setTotalPosts(0)
          }
        }
      } catch (err) {
        console.error("Failed to fetch posts. Please try again.")
      } finally {
        if (append) {
          setLoadingMore(false)
        } else {
          setLoading(false)
        }
      }
    }

    useEffect(() => {
      setCurrentPagePosts([])
      setTotalPosts(0)
      setCurrentPage(1)
      fetchPosts(idCategory, 1)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idCategory, effectiveDomain, siteId, currentLocale])


    return (
      <>
        {(currentPagePosts.length > 0 || loading) && (
          <div className="flex flex-col sm:flex-row justify-center gap-[30px] mx-[20px] sm:mx-[30px]">
            <div className="w-full sm:max-w-[1288px] flex flex-col sm:gap-10 gap-8 sm:pt-10 sm:pb-25 py-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-x-4 sm:gap-y-$ w-full">
                {loading ? (
                  Array.from({ length: postsPerPage }).map((_, index) => (
                    <div key={`loading-${index}`} className="flex-shrink-0 w-full">
                      <div className="flex flex-col gap-4">
                        <div className="relative h-[280px] w-full overflow-hidden rounded-lg bg-gray-200 animate-pulse"></div>

                        <div className="flex flex-col gap-1.5">
                          <div className="flex flex-col">
                            <div className="flex items-center mb-1.5">
                              <div className="h-[5px] w-[5px] bg-gray-300 rounded-full mr-2"></div>
                              <div className="h-4 bg-gray-200 rounded animate-pulse w-24"></div>
                            </div>
                            <div className="h-5 bg-gray-200 rounded animate-pulse w-full mb-1"></div>
                            <div className="h-5 bg-gray-200 rounded animate-pulse w-3/4"></div>
                          </div>
                          <div className="space-y-1">
                            <div className="h-4 bg-gray-200 rounded animate-pulse w-full"></div>
                            <div className="h-4 bg-gray-200 rounded animate-pulse w-5/6"></div>
                            <div className="h-4 bg-gray-200 rounded animate-pulse w-4/5"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <>
                    {currentPagePosts?.map((item, index) => (
                      <PostCardItem key={index} post={item} />
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </>
    )
  },
}
